// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/enterprise-admin/organization/OrganizationProfilePanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
} from "../../../design-system/components";
import BusinessContextSection from "./BusinessContextSection";
import ComplianceContactsSection from "./ComplianceContactsSection";
import FrameworkStrategySection from "./FrameworkStrategySection";
import MissionLaunchHistoryCard from "./MissionLaunchHistoryCard";
import OperationalProfileSection from "./OperationalProfileSection";
import OrganizationHistoryPanel from "./OrganizationHistoryPanel";
import OrganizationOverviewSection from "./OrganizationOverviewSection";

function toCsv(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}

function fromCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function toLines(value) {
  return Array.isArray(value) ? value.join("\n") : "";
}

function fromLines(value) {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function contactDraft(contact = {}) {
  return {
    name: contact?.name || "",
    title: contact?.title || contact?.role || "",
    email: contact?.email || "",
  };
}

function buildDraft(profile = {}) {
  const primaryContact = contactDraft(profile.primary_contact);
  const complianceOwner = contactDraft(profile.compliance_owner);

  return {
    ...profile,
    operating_countries_csv: toCsv(profile.operating_countries),
    selected_frameworks_csv: toCsv(profile.selected_frameworks),
    regions_csv: toCsv(profile.regions),
    business_objectives_text: toLines(profile.business_objectives),
    critical_systems_text: toLines(profile.critical_systems),
    framework_strategy_approach:
      profile?.framework_strategy?.approach || "",
    primary_contact_name: primaryContact.name,
    primary_contact_title: primaryContact.title,
    primary_contact_email: primaryContact.email,
    compliance_owner_name: complianceOwner.name,
    compliance_owner_title: complianceOwner.title,
    compliance_owner_email: complianceOwner.email,
  };
}

function compactObject(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) =>
      String(item || "").trim()
    )
  );
}

function noticeTone(type) {
  if (type === "error" || type === "danger") return "danger";
  if (type === "warning") return "warning";
  return "success";
}

export default function OrganizationProfilePanel({
  profile,
  history = [],
  notice,
  onDismissNotice,
  onSave,
  saving,
}) {
  const initial = useMemo(() => buildDraft(profile || {}), [profile]);
  const [draft, setDraft] = useState(initial);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(initial);
    setDirty(false);
  }, [initial]);

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setDirty(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const primaryContact = compactObject({
      name: draft.primary_contact_name,
      title: draft.primary_contact_title,
      email: draft.primary_contact_email,
    });

    const complianceOwner = compactObject({
      name: draft.compliance_owner_name,
      title: draft.compliance_owner_title,
      email: draft.compliance_owner_email,
    });

    const complianceContacts = [
      Object.keys(primaryContact).length
        ? { contact_type: "primary", ...primaryContact }
        : null,
      Object.keys(complianceOwner).length
        ? {
            contact_type: "compliance_owner",
            ...complianceOwner,
          }
        : null,
    ].filter(Boolean);

    await onSave({
      organization_name: draft.organization_name || "",
      legal_name: draft.legal_name || null,
      industry: draft.industry || null,
      employee_range: draft.employee_range || null,
      headquarters_country: draft.headquarters_country || null,
      operating_countries: fromCsv(draft.operating_countries_csv),
      primary_timezone: draft.primary_timezone || null,
      primary_contact: primaryContact,
      compliance_owner: complianceOwner,
      selected_frameworks: fromCsv(draft.selected_frameworks_csv),
      audit_target_date: draft.audit_target_date || null,
      business_objectives: fromLines(draft.business_objectives_text),
      mission_statement: draft.mission_statement || null,
      critical_systems: fromLines(draft.critical_systems_text),
      framework_strategy: {
        ...(profile?.framework_strategy || {}),
        primary: fromCsv(draft.selected_frameworks_csv),
        approach: draft.framework_strategy_approach || null,
      },
      regions: fromCsv(draft.regions_csv),
      compliance_contacts: complianceContacts,
      organization_status: draft.organization_status || "active",
    });

    setDirty(false);
  };

  return (
    <section className="enterprise-admin-organization-workspace">
      <header className="enterprise-admin-organization-hero">
        <div>
          <p className="enterprise-admin-eyebrow">
            Organization governance
          </p>
          <h2>Organization administration</h2>
          <p>
            Maintain the authoritative enterprise profile used across
            Mission Launch, Mission Control, Mission Execution,
            reporting, and governance.
          </p>
        </div>

        <div className="enterprise-admin-organization-status">
          <span>Status</span>
          <strong>{profile?.organization_status || "active"}</strong>
          <small>
            Updated{" "}
            {profile?.updated_at
              ? new Date(profile.updated_at).toLocaleString()
              : "not yet"}
          </small>
        </div>
      </header>

      {notice ? (
        <Alert
          tone={noticeTone(notice.type)}
          title={notice.message}
          dismissible={Boolean(onDismissNotice)}
          onDismiss={onDismissNotice}
          className="enterprise-admin-organization-notice-canonical"
        />
      ) : null}

      <form
        className="enterprise-admin-organization-form"
        onSubmit={handleSubmit}
      >
        <OrganizationOverviewSection
          draft={draft}
          setField={setField}
        />
        <BusinessContextSection draft={draft} setField={setField} />
        <FrameworkStrategySection
          draft={draft}
          setField={setField}
        />
        <OperationalProfileSection
          draft={draft}
          setField={setField}
        />
        <ComplianceContactsSection
          draft={draft}
          setField={setField}
        />

        <div className="enterprise-admin-organization-savebar">
          <div>
            <strong>
              {dirty
                ? "Unsaved organization changes"
                : "Organization profile is current"}
            </strong>
            <span>
              All saved changes are permission-checked and written to
              administrative audit.
            </span>
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={saving}
            loadingLabel="Saving…"
            disabled={!dirty}
          >
            Save changes
          </Button>
        </div>
      </form>

      <div className="enterprise-admin-organization-bottom-grid">
        <MissionLaunchHistoryCard profile={profile} />
        <OrganizationHistoryPanel history={history} />
      </div>
    </section>
  );
}
